import { CommonDialogs } from "@mcleod/common";
import {
    DataSourceMode, Layout, Panel, SaveButton, SlideoutDecorator, Step, StepEvent, Textbox
} from "@mcleod/components";
import { Api, DateUtil, ModelRow, RowUpdateEvent } from "@mcleod/core";
import { MovementPay } from "./MovementPay";
import { SendRateConfirmation } from "./SendRateConfirmation";
import { AutogenLayoutModifyCarrierPay } from "./autogen/AutogenLayoutModifyCarrierPay";

export class ModifyCarrierPay extends AutogenLayoutModifyCarrierPay {

    public layoutMovePay: MovementPay;
    public layoutSendRateCon: SendRateConfirmation;
    private movementId: string;
    private movementSaved = false;

    private saveButton = SaveButton.create({
        id: "saveCloseButton", width: 160,
        enabled: false, rowBreak: false, marginTop: 2
    });
    private panelButtons = new Panel({ padding: 0, components: [this.saveButton], rowBreak: false });

    advanceStep() {
        this.stepper.selectedIndex++;
    }

    async stepperBeforeStepListener(event: StepEvent) {
        this.panelButtons.removeAll();
        if (event.newIndex == 0) {
            this.panelButtons.add(this.saveButton);
        } else {
            await this.setupStepTwoLayout();
            this.panelButtons.add(this.layoutSendRateCon.buttonSend);
        }
    }

    async setupStepOneLayout(carrierId: string, isCarrierEdi: boolean): Promise<void> {
        const layout = Layout.getLayout("lme/powerbroker/MovementPay", { marginTop: 20 }) as MovementPay;
        layout.carrierId = carrierId;
        const stepLayout = await this.setupStepLayout(layout, { id: this.movementId, payee_id: carrierId }, 0);
        this.layoutMovePay = stepLayout as MovementPay;
        this.layoutMovePay.carrierId = carrierId;
        this.saveButton.dataSource = this.layoutMovePay.mainDataSource;
        this.layoutMovePay.setAllDataSourcesToMode(DataSourceMode.UPDATE);
        this.layoutMovePay.labelRates.visible = false;
        this.layoutMovePay.panelFooter.borderBottomWidth = 0;
        this.layoutMovePay.activeRow.addBeforePostListener(event => this.beforeMovePost(event));
        this.layoutMovePay.mainDataSource.addAfterExecutionListener(() => {
            this.movementSaved = true;
            if (!isCarrierEdi) {
                this.setupStepTwoLayout().then(() => this.advanceStep());
            } else {
                this.slideOut();
            }
        });

        const xfer2SettleDate = this.layoutMovePay.mainDataSource.activeRow?.getDateTime("xfer2settle_date");
        if (xfer2SettleDate) {
            this.layoutMovePay.labelxfer2SettleWarning.text += DateUtil.formatDate(xfer2SettleDate, "MM/dd/yyyy hh:mma");
            this.layoutMovePay.labelxfer2SettleWarning.visible = true;
        }
    }

    async beforeMovePost(event: RowUpdateEvent): Promise<MovementPay> {
        await this.layoutMovePay.calculatePay(false, false, true);
        if (this.layoutMovePay.payError != null) {
            event.preventDefault("invalid data after equipment match applied", () => {
                CommonDialogs.showMessage(this.layoutMovePay.createPayErrorPanel(), "Unable to Save Movement").then(() => {
                    this.layoutMovePay.textboxOverridePayRate.focus();
                    this.layoutMovePay.textboxOverridePayRate.selectText();
                    this.layoutMovePay.activeRow["linkedModels"] = null;
                });
            });
        }
        return this.layoutMovePay;
    }

    async setupStepTwoLayout(): Promise<void> {
        const layout = Layout.getLayout("lme/powerbroker/SendRateConfirmation", { marginTop: 20 }) as SendRateConfirmation;
        const stepLayout = await this.setupStepLayout(layout, { movement_id: this.movementId }, 1);
        this.layoutSendRateCon = stepLayout as SendRateConfirmation;
        this.layoutSendRateCon.panelHeader.visible = false;
        this.layoutSendRateCon.doAfterRateConSent = () => {
            this.slideOut();
        };
    }

    setupStepLayout(layout: MovementPay | SendRateConfirmation, searchFilter: { [key: string]: string }, step: number): Promise<Layout> {
        return new Promise((resolve) => {
            const stepComponent = this.stepper.components[step] as Step;
            stepComponent.removeAll();
            layout.addLayoutLoadListener(() => {
                layout.mainDataSource.search(searchFilter).then(() => {
                    stepComponent.add(layout);
                    resolve(layout);
                });
            });
        });
    }

    private static applyTonuOverrides(layoutMovePay: MovementPay) {
        const modelRow = layoutMovePay.activeRow as ModelRow;
        const overridePayAmount = layoutMovePay.activeRow.get("override_pay_amt");
        if (overridePayAmount !== undefined) {
            overridePayAmount.amount = 0;
            modelRow.set("override_pay_amt", overridePayAmount);
        }
        modelRow.set("override_pay_amt_n", 0);
        modelRow.set("override_type", "F");
        modelRow.set("override_pay_rate", 0);
    }

    private static removeNonTonuRows(layoutMovePay: MovementPay, tonuEarningCode: string) {
        const rowsToDelete = layoutMovePay.sourceExtraPay.data.filter(row => row.get("deduct_code_id") !== tonuEarningCode);
        rowsToDelete.forEach(row => layoutMovePay.sourceExtraPay._deleteTableRow(row, true));
    }

    private static expandAndRefreshPayAdjustmentsTab(layoutMovePay: MovementPay) {
        layoutMovePay.updatePayAdjustmentsTabCaption();
        layoutMovePay.tabPayAdjustment.expandCollapseTab(true, false);
    }

    private static setTonuQuickAddData(quickAddData: ModelRow, carrierId: string, deductCodeId: string, deductCodeLookupRow: ModelRow) {
        quickAddData.setValueAndLookupModelData("payee_id", carrierId, { id: carrierId });
        quickAddData.setValueAndLookupModelData("deduct_code_id", deductCodeId, deductCodeLookupRow);
        quickAddData.set("units", "1");
    }

    private static displayTonuQuickAddData(quickAddData: ModelRow, textboxDeductCodeId: Textbox, textboxPayAdjustmentsPayeeId: Textbox, textboxUnits: Textbox) {
        textboxDeductCodeId.displayData(quickAddData, null, 0);
        textboxPayAdjustmentsPayeeId.displayData(quickAddData, null, 0);
        textboxUnits.displayData(quickAddData, null, 0);
    }

    private static prefillTonuQuickAdd(layoutMovePay: MovementPay, carrierId: string, tonuEarningCode: string) {
        const quickAddRow = layoutMovePay.tableExtraPay.quickAddRow;
        if (!quickAddRow) {
            return;
        }

        const textboxDeductCodeId = quickAddRow.findComponentById("textboxDeductCodeId") as Textbox;
        const textboxPayAdjustmentsPayeeId = quickAddRow.findComponentById("textboxPayAdjustmentsPayeeId") as Textbox;
        const textboxUnits = quickAddRow.findComponentById("textboxUnits") as Textbox;
        if (!textboxDeductCodeId || !textboxPayAdjustmentsPayeeId || !textboxUnits) {
            return;
        }

        Api.search(textboxDeductCodeId.lookupModel, { id: tonuEarningCode }).then(response => {
            if (response?.data[0] != null) {
                const quickAddData = layoutMovePay.tableExtraPay.quickAddData as ModelRow;
                const lmModelRow = textboxDeductCodeId.createLookupModelRow({
                    id: response.data[0].id,
                    short_desc: response.data[0].descr
                });
                this.setTonuQuickAddData(quickAddData, carrierId, response.data[0].id, lmModelRow);
                this.displayTonuQuickAddData(quickAddData, textboxDeductCodeId, textboxPayAdjustmentsPayeeId, textboxUnits);
                layoutMovePay.payAdjustmentsTextboxChanged(textboxUnits);
            }
        });
    }

    public static showSlideout(movementId: string, carrierId: string, doAfterSlideOut: (movementSaved: boolean, moveRow?: ModelRow, extraPayRows?: ModelRow[]) => void, tonuEarningCode?: string, isCarrierEdi?: boolean): Promise<any> {
        const modifyCarrierPay = Layout.getLayout("lme/powerbroker/ModifyCarrierPay", {
            maxWidth: 1150, backgroundColor: "defaultBackground",
            borderRadius: 4, borderWidth: 1, borderShadow: true, borderColor: "strokeSecondary"
        }) as ModifyCarrierPay;
        return new Promise((resolve) => {
            modifyCarrierPay.addLayoutLoadListener(async () => {
                modifyCarrierPay.movementId = movementId;
                if (isCarrierEdi) {
                    modifyCarrierPay.stepper.removeStep(modifyCarrierPay.step2);
                }
                await modifyCarrierPay.setupStepOneLayout(carrierId, isCarrierEdi);
                    const layoutMovePay = modifyCarrierPay.layoutMovePay;
                    if (tonuEarningCode) {
                        this.applyTonuOverrides(layoutMovePay);
                    }
                    SlideoutDecorator.decorateAndSlideIn(modifyCarrierPay, {
                        title: "Modify Carrier Pay",
                        fillVerticalSpace: true,
                        addlComponents: [modifyCarrierPay.panelButtons],
                        doAfterSlideIn: async () => {
                            if (tonuEarningCode) {
                                this.removeNonTonuRows(layoutMovePay, tonuEarningCode);
                                this.expandAndRefreshPayAdjustmentsTab(layoutMovePay);
                                this.prefillTonuQuickAdd(layoutMovePay, carrierId, tonuEarningCode);
                            }
                            this.expandAndRefreshPayAdjustmentsTab(layoutMovePay);
                            layoutMovePay.calculatePay(false);
                        },
                        doAfterSlideOut: () => {
                            if (doAfterSlideOut) {
                                doAfterSlideOut(modifyCarrierPay.movementSaved, modifyCarrierPay.layoutMovePay.activeRow, modifyCarrierPay.layoutMovePay.sourceExtraPay.data);
                            }
                        }
                    });
                resolve(true);
            });
        });
    }
}
